import React, { Fragment } from 'react';

import type { ExtensionHandler } from '@atlaskit/editor-common/extensions/extension-handler';

import { createMacroExtensionHandler } from '@confluence/fabric-extension-lib/entry-points/createMacroExtensionHandler.ts';
import { EXTENSION_KEY } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import {
	EDITOR,
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { InlineExternalImage } from '@confluence/inline-external-image/entry-points/InlineExternalImage.ts';
import type { InlineExternalImageParams } from '@confluence/inline-external-image/entry-points/InlineExternalImage.ts';

import { InvalidExtensionLoader as InvalidExtension } from '../InvalidExtension/InvalidExtensionLoader.tsx';
import { InlineImage } from '../InlineImage/index.ts';
import type { InlineImageParams, ExtensionParamsType } from '../extensionHandlerTypes.ts';
import { LegacyContentExtensionHandler } from '../LegacyContentExtension/index.ts';

export const getMigrationExtHandlerForEditor = ({
	contentId,
	contextProviders,
}: {
	contentId: any;
	contextProviders: any;
}): ExtensionHandler<ExtensionParamsType> =>
	createMacroExtensionHandler((extension) => {
		if (!extension) {
			return null;
		}

		const { extensionKey, parameters } = extension;
		const { ClassicEditorContextProvider = ({ children }) => children } = contextProviders;
		const attributes = getMacroAttributesFromADFNode(extension);
		let implementation;

		const defaultProps = {
			contentId,
			mode: EDITOR,
			extensionKey,
			node: extension,
		};

		switch (extensionKey) {
			case EXTENSION_KEY.INVALID:
				// extension is rendered outside of React DOM tree and require access to the ClassicEditorContext
				implementation = (
					<ClassicEditorContextProvider>
						<InvalidExtension parameters={parameters} {...defaultProps} />
					</ClassicEditorContextProvider>
				);
				break;

			case EXTENSION_KEY.INLINE_EXTERNAL_IMAGE:
				const inlineExtImageProps = parameters as InlineExternalImageParams;
				implementation = <InlineExternalImage {...inlineExtImageProps} {...defaultProps} />;
				break;

			case EXTENSION_KEY.INLINE_MEDIA_IMAGE:
				const inlineMediaImageProps = parameters as InlineImageParams;
				const { link } = parameters;
				implementation = <InlineImage {...inlineMediaImageProps} {...defaultProps} link={link} />;
				break;

			case EXTENSION_KEY.LEGACY_CONTENT:
				implementation = <LegacyContentExtensionHandler {...defaultProps} />;
				break;
		}

		return (
			<Fragment>
				<MacroExperienceStart
					name={getExperienceName(EDITOR, extension)}
					contentId={contentId}
					mode={EDITOR}
					extensionKey={extensionKey}
					attributes={attributes}
					timeout={ExperienceTimeout.MACRO_LOAD}
				/>
				{implementation}
			</Fragment>
		);
	});
